import React from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Autocomplete,
  CircularProgress,
  IconButton,
  Button
} from '@mui/material';
import { X } from 'lucide-react';
import FormField from './FormField';
import ApiSelect from './ApiSelect';
import RichTextEditor from './RichTextEditor';
import { standardInputSx } from '../utils/hubsHelpers';

const BasicDetailsCard = ({
  category,
  onCategoryChange,
  categoryDropdown,
  subCategory,
  onSubCategoryChange,
  subCategoryDropdown,
  selectedCategoryId,
  state,
  onStateChange,
  availableStates,
  town,
  onTownChange,
  availableTowns,
  isOtherTown,
  setIsOtherTown,
  customTown,
  setCustomTown,
  citiesLoading,
  stateIsoCode,
  citiesLoaded,
  fetchBackendCities,
  isAddingCity,
  handleCreateCity,
  hubName,
  setHubName,
  heading,
  setHeading,
  pageSlug,
  setPageSlug,
  locks,
  setLocks,
  applyTemplates,
  introLine,
  setIntroLine,
  descriptionHtml,
  setDescriptionHtml
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: '8px',
        p: '14px 16px',
        mb: 1.75,
        boxShadow: 'none'
      }}
    >
      <Typography variant="subtitle1" sx={{ fontSize: 13.5, fontWeight: 600, color: '#1E293B', mb: 0.35 }}>
        Basic Details
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748B', fontSize: 11, mb: 1.25, lineHeight: 1.4 }}>
        Existing section. Category and SubCategory load from backend on click. State and Town replace free text location name. Change any dropdown and watch the URL, H1 and SEO fields update.
      </Typography>

      {/* Row 1: 3 equal columns */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.25, mb: 1.25 }}>
        <FormField label="Category *">
          <ApiSelect
            value={category}
            onChange={(e) => onCategoryChange(e.target.value)}
            dropdown={categoryDropdown}
            placeholder="Select category..."
            emptyOptionLabel="Select category..."
          />
        </FormField>

        <FormField label="SubCategory">
          <ApiSelect
            value={subCategory}
            onChange={(e) => onSubCategoryChange(e.target.value)}
            dropdown={subCategoryDropdown}
            disabled={!selectedCategoryId}
            placeholder={!selectedCategoryId ? 'Select category first' : 'Select subcategory...'}
            emptyOptionLabel="All SubCategories"
          />
        </FormField>

        <FormField label="State *" badge="NEW">
          <Autocomplete
            size="small"
            fullWidth
            options={availableStates}
            value={state || null}
            onChange={(event, newValue) => onStateChange(newValue || '')}
            isOptionEqualToValue={(option, value) => !value || option === value}
            autoHighlight
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search state..."
                sx={standardInputSx}
              />
            )}
          />
        </FormField>
      </Box>

      {/* Row 2: 3 equal columns with aligned helper notes */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.25, mb: 1.25, alignItems: 'start' }}>
        <FormField
          label="Town / Location Name"
          helperText="Optional. Leave blank for a state level page."
        >
          <Autocomplete
            size="small"
            fullWidth
            options={availableTowns}
            disabled={!state}
            value={isOtherTown ? 'Other' : (town || null)}
            loading={citiesLoading}
            onOpen={() => {
              if (stateIsoCode && !citiesLoaded) {
                fetchBackendCities(stateIsoCode);
              }
            }}
            onChange={(event, newValue) => {
              if (newValue === 'Other') {
                setIsOtherTown(true);
                setCustomTown('');
                onTownChange('');
              } else {
                setIsOtherTown(false);
                setCustomTown('');
                onTownChange(newValue || '');
              }
            }}
            isOptionEqualToValue={(option, value) => !value || option === value}
            autoHighlight
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <li
                  key={key || option}
                  {...otherProps}
                  style={{
                    ...otherProps.style,
                    ...(option === 'Other'
                      ? { fontWeight: 600, color: '#803927', borderTop: '1px dashed #E2E8F0' }
                      : {})
                  }}
                >
                  {option === 'Other' ? '+ Other (Add custom city)' : option}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  !state
                    ? 'Select state first'
                    : isOtherTown
                    ? 'Selected: Other'
                    : 'Whole state (or search city...)'
                }
                sx={standardInputSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {citiesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
          {isOtherTown && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                fullWidth
                autoFocus
                disabled={isAddingCity}
                placeholder="e.g. Mumbai, Varkala..."
                value={customTown}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateCity();
                  }
                }}
                onChange={(e) => {
                  const val = e.target.value;
                  setCustomTown(val);
                  onTownChange(val);
                }}
                sx={standardInputSx}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      title="Cancel custom city"
                      disabled={isAddingCity}
                      onClick={() => {
                        setIsOtherTown(false);
                        setCustomTown('');
                        onTownChange('');
                      }}
                      sx={{ color: '#64748B', '&:hover': { color: '#803927' } }}
                    >
                      <X size={14} />
                    </IconButton>
                  )
                }}
              />
              <Button
                variant="contained"
                size="small"
                disabled={isAddingCity || !customTown.trim()}
                onClick={handleCreateCity}
                sx={{
                  backgroundColor: '#FFBD65',
                  color: '#1E293B',
                  fontWeight: 500,
                  fontSize: 12,
                  height: 34,
                  minWidth: 80,
                  borderRadius: '6px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: '#F5A947', boxShadow: 'none' },
                  '&.Mui-disabled': { backgroundColor: '#F1F5F9', color: '#94A3B8' }
                }}
              >
                {isAddingCity ? <CircularProgress size={14} sx={{ color: '#1E293B' }} /> : 'Add City'}
              </Button>
            </Box>
          )}
        </FormField>

        <FormField
          label="Hub Name *"
          helperText={
            locks?.name ? (
              <span>
                Suggested format: Category Name in Location Name ·{' '}
                <Box
                  component="span"
                  onClick={() => {
                    setLocks((prev) => ({ ...prev, name: false }));
                    applyTemplates(true);
                  }}
                  sx={{ color: '#803927', fontWeight: 600, cursor: 'pointer' }}
                >
                  reset to auto
                </Box>
              </span>
            ) : (
              'Suggested format: Category Name in Location Name'
            )
          }
        >
          <TextField
            size="small"
            fullWidth
            value={hubName}
            placeholder="e.g. Ayurveda Retreats in Kerala"
            onChange={(e) => {
              setHubName(e.target.value);
              setLocks((prev) => ({ ...prev, name: true }));
            }}
            sx={standardInputSx}
          />
        </FormField>

        <FormField
          label="Heading (H1)"
          helperText={
            locks?.h1 ? (
              <span>
                Renders as the single H1 on the page ·{' '}
                <Box
                  component="span"
                  onClick={() => {
                    setLocks((prev) => ({ ...prev, h1: false }));
                    applyTemplates(true);
                  }}
                  sx={{ color: '#803927', fontWeight: 600, cursor: 'pointer' }}
                >
                  reset to auto
                </Box>
              </span>
            ) : (
              'Renders as the single H1 on the page.'
            )
          }
        >
          <TextField
            size="small"
            fullWidth
            value={heading}
            placeholder="e.g. Best Ayurveda Retreats in Kerala"
            onChange={(e) => {
              setHeading(e.target.value);
              setLocks((prev) => ({ ...prev, h1: true }));
            }}
            sx={standardInputSx}
          />
        </FormField>
      </Box>

      {/* Page URL */}
      <FormField
        label="Page URL"
        badge="NEW"
        helperText={
          <span>
            Live at <b>https://healatindia.com{pageSlug}</b> · built from Category, SubCategory, State, Town and Month ·{' '}
            {locks?.slug ? (
              <span>
                edited by hand,{' '}
                <Box
                  component="span"
                  onClick={() => {
                    setLocks((prev) => ({ ...prev, slug: false }));
                    applyTemplates(true);
                  }}
                  sx={{ color: '#803927', fontWeight: 600, cursor: 'pointer' }}
                >
                  reset to auto
                </Box>
              </span>
            ) : (
              'updates automatically'
            )}{' '}
            ·{' '}
            <Box
              component="span"
              onClick={() => {
                const newSlug = prompt('Type custom page URL path:', pageSlug);
                if (newSlug !== null && newSlug.trim()) {
                  setPageSlug(newSlug.trim());
                  setLocks((prev) => ({ ...prev, slug: true }));
                }
              }}
              sx={{ color: '#803927', fontWeight: 600, cursor: 'pointer' }}
            >
              edit by hand
            </Box>
          </span>
        }
        sx={{ mb: 1.25 }}
      >
        <TextField
          size="small"
          fullWidth
          value={pageSlug}
          onChange={(e) => {
            setPageSlug(e.target.value);
            setLocks((prev) => ({ ...prev, slug: true }));
          }}
          sx={standardInputSx}
        />
      </FormField>

      {/* Intro line */}
      <FormField
        label="Intro line"
        badge="NEW"
        helperText="One sentence, shows directly under the H1."
        sx={{ mb: 1.25 }}
      >
        <Box sx={{ position: 'relative' }}>
          <TextField
            size="small"
            fullWidth
            value={introLine}
            inputProps={{ maxLength: 140 }}
            onChange={(e) => setIntroLine(e.target.value)}
            sx={{ ...standardInputSx, '& .MuiOutlinedInput-input': { pr: 7 } }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: 12,
              top: 10,
              fontSize: 11,
              fontWeight: 500,
              color: introLine.length > 120 ? '#DC2626' : '#2E7D32',
              pointerEvents: 'none'
            }}
          >
            {introLine.length}/140
          </Box>
        </Box>
      </FormField>

      {/* Description */}
      <FormField
        label="Description (short, above the retreat list)"
        badge="NEW: rich text"
      >
        <RichTextEditor
          value={descriptionHtml}
          onChange={setDescriptionHtml}
          minHeight={110}
        />
      </FormField>
    </Paper>
  );
};

export default React.memo(BasicDetailsCard);
